import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { InputJson } from '@core/models/built/session-step-value-json.model';
import { SessionStep, SessionStepValue } from '@core/models/session.model';
import { ReportSessionService } from '@core/services/features/report-session.service';
import { TranslocoService } from '@ngneat/transloco';
import { ChartDataSets } from 'chart.js';
import { DateTime } from 'luxon';
import { Color, Label } from 'ng2-charts';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import it from './i18n/external-ws-report.it.json';

interface InputExternalWs {
  input: InputExternalWsInput;
}

interface InputExternalWsInput {
  cycleCounter: number;
  programCode: number;
  key: string;
  label: string;
  value: number;
  unitOfMeasure: string;
}

@Component({
  templateUrl: './external-ws-report.component.html'
})
export class ExternalWsReportComponent implements OnChanges {

  @Input() step!: SessionStep;

  private step$$ = new BehaviorSubject<SessionStep>(this.step);
  step$ = this.step$$.asObservable();

  ssvs$ = this.step$.pipe(map(step => step.values || []), shareReplay());

  showValues$ = this.ssvs$.pipe(map(values => values && values.length > 0));


  valuesList$ = this.ssvs$.pipe(map(ssvs => ssvs.map(ssv => {
    const { label, value, unitOfMeasure } = (ssv.value as InputExternalWs).input;
    return { timestamp: ssv.timestamp, label: `${label} (${unitOfMeasure})`, value };
  })));

  inputs$ = this.ssvs$.pipe(
    map<SessionStepValue[], InputExternalWsInput[]>(ssvs => ssvs.map(ssv => (ssv.value as InputExternalWs).input)),
    shareReplay()
  );

  commonInputData$ = this.inputs$.pipe(
    map(inputs => {
      if (inputs.length > 0) {
        const { cycleCounter, programCode } = inputs[0];
        return { cycleCounter, programCode };
      } else {
        return {};
      }
    })
  );

  chartDatasets$: Observable<ChartDataSets[]> = this.inputs$.pipe(
    map(inputs => {
      const dataMap = inputs.reduce((acc, input) => {
        const { label, unitOfMeasure } = input;
        const l = `${label} (${unitOfMeasure})`;
        const prevValues = acc[l] || [];
        return {
          ...acc,
          ...{ [l]: [...prevValues, input] }
        };
      }, {} as { [key: string]: InputExternalWsInput[] });

      const datasets: ChartDataSets[] = Object.entries(dataMap).map(([k, v]) => ({
        label: k,
        data: v.map(vv => vv.value)
      }));
      return datasets;
    })
  );

  // qui mappo i timestamp
  chartLabels$: Observable<Label[]> = this.ssvs$.pipe(
    map(values => values.map(v => DateTime.fromISO(v.timestamp as string).toFormat('HH:mm:ss')))
  );

  lineChartColors: Color[] = [
    { // red
      backgroundColor: 'rgba(60, 183, 132, 0.3)',
      borderColor: 'rgba(60, 183, 132, 1)',
      pointBackgroundColor: 'rgba(4, 73, 33, 1)',
      pointBorderColor: '#fff',
      pointHoverBackgroundColor: '#fff',
      pointHoverBorderColor: 'rgba(148,159,177,0.8)'
    }
  ];

  constructor(
    private translocoSvc: TranslocoService,
    private reportSessionSvc: ReportSessionService
  ) {
    this.translocoSvc.setTranslation(it, 'it');
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.step) {
      this.step$$.next(changes.step.currentValue);
    }
  }

  loadValues(id: number) {
    this.reportSessionSvc.getValuesByStepId(id).subscribe(values => {
      const step = this.step$$.getValue();
      this.step$$.next({ ...step, values });
    });
  }
}
